/* eslint-disable max-len */
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ActionButton,
  ActionButtons,
  Badge,
  Button,
  DataTable,
  EmptyState,
  SearchWithFilter,
  type DataTableColumn,
  type DataTableSortDirection,
  type FilterSelection,
} from '@enamelai/design-system'
import { Icon as DeleteFilledIcon } from '@enamelai/design-system/icons/Delete2'
import { Icon as NotesIcon } from '@enamelai/design-system/icons/Notes'
import './css/PatientManagement.css'
import AddPatientModal from '@components/layout/Modals/AddPatientModal/AddPatientModal.component'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  fetchAllPatients,
  removePatient,
  updatePatient,
  addPatient,
} from '@store/actions/patientmanagement.action'
import { Patient } from '@dataTypes/PatientInfo.types'
import EmailSender from '@components/layout/Modals/EmailSender/EmailSender.component'
import { getFullName } from '@utils/fullname.util'
import {
  aiReceptionistActorHint,
  AI_PATIENT_FILTER_OPTIONS,
  matchesAiReceptionistRecordFilter,
} from '@utils/aiReceptionist.util'
import { useAiReceptionistActorId } from '@hooks/useAiReceptionistActorId'
import { AiReceptionistIcon } from '@components/common'
import { setSelectedPatient } from '@store/reducers/patientmanagement.reducer'
import { setActiveSidePanel } from '@store/reducers/uiSlice.reducer'
import { StatusTypes } from '@components/common/StatusBadge/StatusBadge.component'
import { fetchAllCarriers } from '@store/actions/carrier.action'
import {
  DeleteConfirmationModal,
  ScheduleAppointment,
  SelectContactModal,
} from '@components/layout/Modals'
import {
  AppointmentRequest,
  PatientWithInsurance,
} from '@dataTypes/appointment.type'
import moment from 'moment'
import { createAppointment } from '@services/api/appointment.api'
import { fetchAllAppointmentTypes, fetchAllContacts } from '@store/actions'
import useContact from '@components/pages/Messages/MessageCenterContact/useContact'
import { IContact } from '@dataTypes/contact.type'
import { useClinicOperatories, useClinicProviders } from '@hooks'
import {
  PATIENT_STATUS,
  PATIENT_STATUS_BADGE_COLOR,
} from '@constants/PatientManagement/status'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { useNoteEditorOverlay } from '@components/common/NoteEditorOverlay'
import { formatCurrency } from '@utils/billing.utils'

import { logger } from '@utils/safeLogger'

type PatientRow = { id: string }

type PatientSortColumn =
  | 'name'
  | 'previous_visit'
  | 'next_visit'
  | 'balance_due'
  | 'status'

const DEFAULT_PAGE_SIZE = 10
const GLOBAL_LOADING_CONTAINER = 'body'

const getPatientStatus = (patient: Patient): StatusTypes =>
  patient.patient_status !== undefined
    ? (PATIENT_STATUS[patient.patient_status] ?? StatusTypes.Active)
    : StatusTypes.Active

const getBalanceDue = (patient: Patient): number => {
  const balance = Number(patient.balance_due ?? 0)
  return Number.isFinite(balance) ? balance : 0
}

const formatVisitDate = (value?: string | null): string | null =>
  value ? moment(value, 'YYYY-MM-DD HH:mm:ss').format('MM/DD/YYYY') : null

const getSortValue = (
  patient: Patient,
  column: PatientSortColumn,
): string | number | null => {
  switch (column) {
    case 'name':
      return getFullName(patient).toLowerCase()
    case 'previous_visit':
      return patient.date_previous_visit || null
    case 'next_visit':
      return patient.date_next_visit || null
    case 'balance_due':
      return getBalanceDue(patient)
    case 'status':
      return getPatientStatus(patient)
  }
}

const PatientManagement: React.FC = () => {
  useClinicOperatories()
  useClinicProviders()
  const navigate = useNavigate()
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [searchFilters, setSearchFilters] = useState<FilterSelection>({})
  const [editPatData, setEditPatData] = useState<Patient | null>(null)
  const [showAddPatientModal, setShowAddPatientModal] = useState(false)
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false)
  const [patientToDelete, setPatientToDelete] = useState<Patient | null>(null)
  const [emailRecipient, setEmailRecipient] = useState<string | null>(null)
  const [patientsFetchDone, setPatientsFetchDone] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sortColumnId, setSortColumnId] = useState<PatientSortColumn | null>(
    'name',
  )
  const [sortDirection, setSortDirection] =
    useState<DataTableSortDirection>('asc')
  const [isSchedulingModalOpen, setIsSchedulingModalOpen] =
    useState<boolean>(false)
  const { openNoteEditor } = useNoteEditorOverlay()

  const dispatch = useAppDispatch()
  const { selectedPatient } = useAppSelector((state) => state.patient)
  const patientsData = useAppSelector((state) => state.patient.data)
  const patientsState = useMemo(() => patientsData || [], [patientsData])
  const patientsById = useMemo(() => {
    const map = new Map<string, Patient>()
    patientsState.forEach((patient) => map.set(patient.patient_id, patient))
    return map
  }, [patientsState])
  const { ...contactsHooks } = useContact()
  const isHIPAAView = useAppSelector((state) => state.ui.isHIPAAView)
  const contacts = useAppSelector((state) => state.contacts.data)
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const aiActorId = useAiReceptionistActorId()
  const { showToast } = useNotificationToast()

  useEffect(() => {
    let cancelled = false

    setPatientsFetchDone(false)
    dispatch(fetchAllCarriers())
    dispatch(fetchAllAppointmentTypes())
    dispatch(fetchAllPatients()).finally(() => {
      if (!cancelled) {
        setPatientsFetchDone(true)
      }
    })

    return () => {
      cancelled = true
    }
  }, [dispatch, selectedClinic])

  useEffect(() => {
    if (!patientsFetchDone) {
      showLoading({
        container: GLOBAL_LOADING_CONTAINER,
        hideElements: ['.patient-management__content'],
      })
    } else {
      hideLoading(GLOBAL_LOADING_CONTAINER)
    }
    return () => hideLoading(GLOBAL_LOADING_CONTAINER)
  }, [patientsFetchDone, showLoading, hideLoading])

  const onDeletePatient = (patient: Patient): void => {
    setPatientToDelete(patient)
    setShowConfirmDeleteModal(true)
  }

  const confirmDeletePatient = useCallback(async (): Promise<void> => {
    if (patientToDelete) {
      showLoading({
        container: '.patient-delete-modal',
      })
      try {
        await dispatch(removePatient(patientToDelete.patient_id)).unwrap()
        setTimeout(() => {
          hideLoading('.patient-delete-modal')
          showToast(
            createSuccessToast(
              'Patient deleted successfully',
              'Patient has been deleted successfully.',
            ),
          )
        }, 1000)
      } catch (e) {
        logger.error('Error deleting patient:', e)
        setTimeout(() => {
          hideLoading('.patient-delete-modal')
          showToast(
            createFailedToast(
              'Patient not deleted',
              'Patient not deleted. Please try again.',
            ),
          )
        }, 1000)
      } finally {
        setShowConfirmDeleteModal(false)
      }
    }
  }, [dispatch, showLoading, hideLoading, showToast, patientToDelete])

  const onEditPatient = (patient: Patient): void => {
    setEditPatData(patient)
    setShowAddPatientModal(true)
  }

  /** Makes the patient active (row, tray, side panel content) without opening the panel. */
  const selectPatient = (patient: Patient): void => {
    const contact = contacts?.find((c) => c.patient_id === patient.patient_id)
    dispatch(setSelectedPatient({ patient, contact }))
  }

  const onShowPatientInfo = (patient: Patient): void => {
    dispatch(setActiveSidePanel('main'))
    selectPatient(patient)
  }

  const handleTableClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    const target = event.target as HTMLElement
    if (target.closest('button, a, input, textarea, select, [role="menu"]')) {
      return
    }
    const row = target.closest('tr.ds-data-table__row')
    const patientId = row
      ?.querySelector<HTMLElement>('[data-patient-id]')
      ?.getAttribute('data-patient-id')
    const patient = patientId ? patientsById.get(patientId) : undefined
    if (patient) onShowPatientInfo(patient)
  }

  const handleAddPatientClick = (): void => {
    setShowAddPatientModal(true)
  }

  const handleCloseAddPatientModal = (): void => {
    setEditPatData(null)
    setShowAddPatientModal(false)
  }

  const cleanEmptyNullableFields = <T extends Record<string, any>>(
    data: T,
  ): Partial<T> => {
    const cleaned: Partial<T> = { ...data }

    for (const key in cleaned) {
      const typedKey = key as keyof T
      if (cleaned[typedKey] === '') {
        delete cleaned[typedKey]
      }
    }

    return cleaned
  }

  const findPatientContact = (patient: Patient): IContact | undefined => {
    const patientContact = contactsHooks.filteredContacts.find(
      (contact) => contact.patient_id === patient.patient_id,
    )
    if (!patientContact) {
      showToast(
        createFailedToast(
          'Contact not found',
          `${getFullName(patient)} has no contact record yet.`,
        ),
      )
    }
    return patientContact
  }

  const handlePhoneClick = (patient: Patient): void => {
    const patientContact = findPatientContact(patient)
    if (patientContact) {
      contactsHooks.handlePhoneAction(patientContact)
    }
  }

  const handleMessageClick = (patient: Patient): void => {
    const patientContact = findPatientContact(patient)
    if (patientContact) {
      contactsHooks.handleSendMessage(patientContact)
      navigate('/message')
    }
  }

  const openPatientNotes = (patient: Patient): void => {
    openNoteEditor({
      patient,
      openFirstNote: true,
      pagePreviousState: { path: '/patient-management' },
      initialView: 'list',
      hideNotesTable: true,
      overlayBackLabel: 'Back to patient management',
      editorBackButtonLabel: 'Back to patient management',
      openContextNotesByDefault: true,
      notesFetchLoadingContainer: 'body',
      portalTarget: '.page-content',
    })
  }

  const withPatient =
    (render: (patient: Patient) => React.ReactNode) =>
    (row: PatientRow): React.ReactNode => {
      const patient = patientsById.get(row.id)
      return patient ? render(patient) : null
    }

  const columns: DataTableColumn<PatientRow>[] = [
    {
      id: 'name',
      label: 'Patient name',
      sortable: true,
      renderCell: withPatient((patient) => {
        const hint = aiReceptionistActorHint(patient, aiActorId, 'patient')
        return (
          <span
            className="patient-management__name ai-receptionist-name"
            data-patient-id={patient.patient_id}
          >
            <span className="ai-receptionist-name__text">
              {getFullName(patient, false, isHIPAAView)}
            </span>
            {hint ? <AiReceptionistIcon label={hint} /> : null}
          </span>
        )
      }),
    },
    {
      id: 'previous_visit',
      label: 'Previous visit',
      sortable: true,
      width: 140,
      renderCell: withPatient(
        (patient) => formatVisitDate(patient.date_previous_visit) ?? 'N/A',
      ),
    },
    {
      id: 'next_visit',
      label: 'Next visit',
      sortable: true,
      width: 140,
      renderCell: withPatient(
        (patient) =>
          formatVisitDate(patient.date_next_visit) ?? (
            <span className="patient-management__unscheduled">Unscheduled</span>
          ),
      ),
    },
    {
      id: 'balance_due',
      label: 'Balance due',
      sortable: true,
      width: 130,
      renderCell: withPatient((patient) => {
        const balance = getBalanceDue(patient)
        return (
          <span
            className={`patient-management__balance${
              balance !== 0 ? ' patient-management__balance--due' : ''
            }`}
          >
            {formatCurrency(balance)}
          </span>
        )
      }),
    },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      width: 150,
      renderCell: withPatient((patient) => {
        const status = getPatientStatus(patient)
        return (
          <Badge
            label={status}
            mode="tinted"
            color={PATIENT_STATUS_BADGE_COLOR[status] ?? 'gray'}
          />
        )
      }),
    },
    {
      id: 'notes',
      label: 'Notes',
      align: 'center',
      width: 80,
      renderCell: withPatient((patient) =>
        (patient.total_notes || 0) > 0 ? (
          <ActionButton
            className="patient-management__notes-button"
            icon={
              <NotesIcon className="ds-action-button__svg" aria-hidden="true" />
            }
            label="View notes"
            tone="primary"
            variant="outline"
            onClick={() => openPatientNotes(patient)}
          />
        ) : (
          '-'
        ),
      ),
    },
  ]

  const renderPatientActions = withPatient((patient) => (
    <ActionButtons
      onView={() => onShowPatientInfo(patient)}
      onEdit={() => onEditPatient(patient)}
      middle={
        <ActionButton
          icon={
            <DeleteFilledIcon
              className="ds-action-button__svg"
              aria-hidden="true"
            />
          }
          label="Delete"
          tone="danger"
          variant="outline"
          onClick={() => onDeletePatient(patient)}
        />
      }
      onPhone={() => handlePhoneClick(patient)}
      onMail={
        patient.email ? () => setEmailRecipient(patient.email) : undefined
      }
      onMessage={() => handleMessageClick(patient)}
    />
  ))

  const handleSort = (columnId: string): void => {
    const column = columnId as PatientSortColumn
    if (sortColumnId !== column) {
      setSortColumnId(column)
      setSortDirection('asc')
    } else if (sortDirection === 'asc') {
      setSortDirection('desc')
    } else {
      // Third click returns to the default A-Z order on the name column.
      setSortColumnId('name')
      setSortDirection('asc')
    }
    setCurrentPage(1)
  }

  const handleSaveAppointment = (
    apptDetails: Partial<AppointmentRequest>,
  ): void => {
    createAppointment(apptDetails)
  }

  const filterData: Record<string, string[]> = useMemo(() => {
    if (!Array.isArray(patientsState)) {
      return {
        status: [],
        aiReceptionist: AI_PATIENT_FILTER_OPTIONS,
      }
    }

    const uniqueStatuses = Array.from(
      new Set(
        patientsState
          .map((p) => {
            if (p.patient_status !== undefined) {
              return PATIENT_STATUS[p.patient_status]
            }
            return StatusTypes.Active
          })
          .filter((status): status is StatusTypes => Boolean(status)),
      ),
    ).map((status) => status.toString())

    return {
      status: uniqueStatuses,
      aiReceptionist: AI_PATIENT_FILTER_OPTIONS,
    }
  }, [patientsState])

  const filterLabels = {
    status: 'Status',
    aiReceptionist: 'AI receptionist',
  }

  const handleSearchChange = (value: string): void => {
    setSearchQuery(value)
    setCurrentPage(1)
  }

  const handleFilterChange = (selectedFilters: FilterSelection): void => {
    setSearchFilters(selectedFilters)
    setCurrentPage(1)
  }

  const clearAllFilters = (): void => {
    setSearchFilters({})
    setSearchQuery('')
    setCurrentPage(1)
  }

  const filteredPatients = useMemo((): Patient[] => {
    if (!Array.isArray(patientsState)) return []
    let dataToFilter = patientsState

    if (searchQuery.trim()) {
      dataToFilter = patientsState.filter((patient) => {
        const fullName = getFullName(patient)
        const datePreviousVisit = patient.date_previous_visit
          ? moment(patient.date_previous_visit).format('MM/DD/YYYY')
          : 'N/A'
        const dateNextVisit = patient.date_next_visit
          ? moment(patient.date_next_visit).format('MM/DD/YYYY')
          : 'Unscheduled'
        return (
          fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          patient.public_id
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          datePreviousVisit.toLowerCase().includes(searchQuery.toLowerCase()) ||
          dateNextVisit.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })
    }

    return dataToFilter.filter((patient) => {
      if (searchFilters.status && searchFilters.status.length > 0) {
        if (!searchFilters.status.includes(getPatientStatus(patient))) {
          return false
        }
      }
      return matchesAiReceptionistRecordFilter(
        patient,
        aiActorId,
        searchFilters.aiReceptionist,
        'patient',
      )
    })
  }, [searchQuery, patientsState, searchFilters, aiActorId])

  const sortedPatients = useMemo((): Patient[] => {
    if (!sortColumnId) return filteredPatients
    const direction = sortDirection === 'asc' ? 1 : -1
    return [...filteredPatients].sort((a, b) => {
      const aValue = getSortValue(a, sortColumnId)
      const bValue = getSortValue(b, sortColumnId)
      if (aValue === bValue) return 0
      if (aValue === null) return 1
      if (bValue === null) return -1
      return aValue < bValue ? -direction : direction
    })
  }, [filteredPatients, sortColumnId, sortDirection])

  const totalPages = Math.max(1, Math.ceil(sortedPatients.length / pageSize))
  const page = Math.min(currentPage, totalPages)

  const pageRows = useMemo(
    (): PatientRow[] =>
      sortedPatients
        .slice((page - 1) * pageSize, page * pageSize)
        .map((patient) => ({ id: patient.patient_id })),
    [sortedPatients, page, pageSize],
  )

  const selectedRowIds = useMemo(
    () => (selectedPatient?.patient_id ? [selectedPatient.patient_id] : []),
    [selectedPatient?.patient_id],
  )

  const hasNoPatients =
    !Array.isArray(patientsState) || patientsState.length === 0
  const showEmptyState = patientsFetchDone && filteredPatients.length === 0
  const disableHeaderActions = showEmptyState && hasNoPatients

  const handleSavePatient = async (patientData: Patient): Promise<void> => {
    if (patientData.patient_id) {
      await dispatch(updatePatient(patientData)).then(async () => {
        await dispatch(fetchAllContacts())
      })
    } else {
      const cleanedData = cleanEmptyNullableFields(patientData)
      await dispatch(addPatient(cleanedData)).then(async () => {
        await dispatch(fetchAllContacts())
      })
    }
  }

  return (
    <div className="patient-management">
      <div className="patient-management__toolbar">
        <h1 className="patient-management__title">Patient management</h1>
        <div className="patient-management__actions">
          <SearchWithFilter
            className="patient-management__search"
            placeholder="Search patients..."
            value={searchQuery}
            onChange={handleSearchChange}
            filters={filterData}
            filterLabels={filterLabels}
            selectedFilters={searchFilters}
            onFilterChange={handleFilterChange}
            showResetButton
            onReset={clearAllFilters}
          />
          <Button
            variant="secondary"
            size="md"
            onClick={handleAddPatientClick}
            disabled={disableHeaderActions}
          >
            Add patient
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => setIsSchedulingModalOpen(true)}
            disabled={disableHeaderActions}
          >
            Set appointment
          </Button>
        </div>
      </div>
      <div className="patient-management__content" onClick={handleTableClick}>
        {showEmptyState ? (
          <EmptyState
            className="patient-management__empty"
            iconClass="mgc mgc_user_search_fill"
            title="No patients found"
            description={
              hasNoPatients
                ? "You don't have any patients on record yet."
                : 'No patients match your search.'
            }
            actionLabel={hasNoPatients ? 'Add patient' : undefined}
            onAction={hasNoPatients ? handleAddPatientClick : undefined}
          />
        ) : (
          <DataTable<PatientRow>
            className="patient-management__table"
            columns={columns}
            rows={pageRows}
            state={patientsFetchDone ? 'default' : 'loading'}
            selectedRowIds={selectedRowIds}
            actionsLabel="Actions"
            renderActions={renderPatientActions}
            sortColumnId={sortColumnId ?? undefined}
            sortDirection={sortColumnId ? sortDirection : undefined}
            onSort={handleSort}
            pagination={{
              currentPage: page,
              totalPages,
              pageSize,
              onPageChange: setCurrentPage,
              onPageSizeChange: (size) => {
                setPageSize(size)
                setCurrentPage(1)
              },
            }}
          />
        )}
      </div>

      {showAddPatientModal && (
        <AddPatientModal
          isOpen={showAddPatientModal}
          onRequestClose={handleCloseAddPatientModal}
          onSave={handleSavePatient}
          patientData={editPatData ?? undefined}
        />
      )}

      <DeleteConfirmationModal
        show={showConfirmDeleteModal}
        onHide={() => setShowConfirmDeleteModal(false)}
        onConfirm={confirmDeletePatient}
        title="Delete patient?"
        bodyText={`Are you sure you want to delete ${patientToDelete ? getFullName(patientToDelete) : 'this patient'}? This action can't be undone.`}
      />
      {isSchedulingModalOpen && (
        <ScheduleAppointment
          isOpen={isSchedulingModalOpen}
          handleModal={() => setIsSchedulingModalOpen(false)}
          onSave={handleSaveAppointment}
          initialStartTime={moment().format('YYYY-MM-DD')}
          initialEndTime={moment().format('YYYY-MM-DD')}
          initialRoom={undefined}
          initialProvider={undefined}
          currentAppt={undefined}
          {...(selectedPatient && {
            preSelectedPatient: {
              patient_id: selectedPatient.patient_id,
              first_name: selectedPatient.first_name,
              last_name: selectedPatient.last_name,
              has_insurance: selectedPatient.has_insurance,
              pat_ins: selectedPatient.pat_ins,
            } as PatientWithInsurance,
          })}
        />
      )}
      {contactsHooks.showSelectContactModal && (
        <SelectContactModal
          show={contactsHooks.showSelectContactModal}
          onHide={contactsHooks.handleCloseSelectContactModal}
          contactData={contactsHooks.selectedContact as IContact}
        />
      )}
      <EmailSender
        show={emailRecipient !== null}
        onHide={() => setEmailRecipient(null)}
        recipientEmail={emailRecipient ?? ''}
      />
    </div>
  )
}

export default PatientManagement
